// Pathway taxonomy for the public patient-prospect signup flow.
//
// Pre-login we ask ONE question: "What kind of care are you looking for?"
// Each pathway maps to a backend `provisionalPathway` enum value (see
// docs/care_orchestration_system.md) plus a finer-grained `specialtyTag`
// used internally for routing and dashboards. None of these are exposed
// as a clinic-brand list — brand assignment happens server-side later.
//
// The blurb is the only thing the user reads when picking; keep it short
// and pitched as "the care this finds you", not as the medical category.

// User-facing copy: avoid clinical jargon as the headline. "Psychiatry" /
// "Psychology" / "Endocrinology" are still inside the blurb so people who
// know the term can recognise themselves, but the label is the plain
// version a non-medical visitor would type into search.
export const PATHWAYS = [
  {
    id: 'mental-health',
    pathway: null, // resolved by sub-pick
    label: 'Mental health',
    blurb: 'Feeling anxious, low, overwhelmed, struggling with sleep, focus, or relationships.',
    iconName: 'Brain',
    accent: '#7B61FF',
    subOptions: [
      {
        id: 'mental-psychology',
        pathway: 'THERAPY',
        specialtyTag: 'psychology',
        label: 'Talk to a therapist',
        blurb: 'Conversations with a clinical psychologist or counsellor — work through how you’re feeling, no medication.',
      },
      {
        id: 'mental-psychiatry',
        pathway: 'PSYCHIATRY',
        specialtyTag: 'psychiatry',
        label: 'Speak with a doctor about medication',
        blurb: 'Meet a psychiatrist (an MD) to evaluate whether medication might help, and to manage prescriptions.',
      },
      {
        id: 'mental-help-decide',
        // Maps to the assessment funnel so the care coordinator can
        // recommend therapy / medication / both based on a short chat.
        // Tagged 'mental-health-triage' so support dashboards can tell it
        // apart from the broader 'triage' picks at the top level.
        pathway: 'ASSESSMENT',
        specialtyTag: 'mental-health-triage',
        label: 'Help me decide',
        blurb: 'Not sure if therapy or medication fits. We’ll have a short chat first and recommend what’s right.',
      },
    ],
  },
  {
    id: 'hormonal-health',
    pathway: 'BIOLOGICAL',
    specialtyTag: 'endocrinology',
    label: 'Hormonal health',
    blurb: 'Thyroid, diabetes, PCOS, weight, or menstrual concerns — meet a hormone specialist (endocrinologist).',
    iconName: 'HeartPulse',
    accent: '#E27D60',
  },
  {
    id: 'immune-system',
    pathway: 'BIOLOGICAL',
    specialtyTag: 'rheumatology',
    label: 'Immune system',
    blurb: 'Autoimmune flares, joint or skin trouble, unexplained inflammation — meet an immune specialist (rheumatologist).',
    iconName: 'ShieldCheck',
    accent: '#41B883',
  },
  {
    id: 'child-growth',
    pathway: 'CHILD',
    specialtyTag: 'pediatrics',
    label: 'Child growth & care',
    blurb: 'Growth, development, behaviour, nutrition or sleep concerns for a child — meet a pediatrician.',
    iconName: 'Baby',
    accent: '#3D9ED7',
  },
  {
    id: 'not-sure',
    pathway: 'ASSESSMENT',
    specialtyTag: 'triage',
    label: 'Not sure — help me figure it out',
    blurb: 'Tell us what’s going on. Our care team will help you decide what kind of specialist fits.',
    iconName: 'Puzzle',
    accent: '#8492A6',
  },
]

// Resolve a pathway selection (top-level + optional sub-option id) to the
// canonical { pathway, specialtyTag, label } the backend expects.
export function resolvePathway(topLevelId, subId) {
  const top = PATHWAYS.find((p) => p.id === topLevelId)
  if (!top) return null
  if (top.subOptions) {
    const sub = top.subOptions.find((s) => s.id === subId)
    if (!sub) return null
    return {
      pathway: sub.pathway,
      specialtyTag: sub.specialtyTag,
      topLabel: top.label,
      subLabel: sub.label,
    }
  }
  return {
    pathway: top.pathway,
    specialtyTag: top.specialtyTag,
    topLabel: top.label,
    subLabel: null,
  }
}

export const PROSPECT_DRAFT_KEY = 'patientProspectSignupDraft'
export const PROSPECT_DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000
